import React, { useMemo, useState } from 'react';
import { computeWinningPathByIndividualId } from '../utils/mrcaAssignedDna';
import { buildMrcaPathColorMap } from '../utils/mrcaLineageModel';
import ChromosomePainterAncestryRegionLayer from './ChromosomePainterAncestryRegionLayer';
import { ANCESTRY_REGIONS_BY_PARENT, type AncestryRegionByParentSeg } from '../data/ancestryRegionsByParent';

const chromosomePainterMrcaPathBarColors = buildMrcaPathColorMap();

export interface Segment {
  start: number;
  end: number;
  chromosome: number;
  progenitor_id: string;
  progenitor_name: string;
  progenitor_color: string;
  match_name: string;
  centimorgans: number;
  snps: number;
  segment_id: string;
  match_id: string;
}

export interface ChromPainterData {
  chromosomes: { [chrom: string]: { paternal: Segment[]; maternal: Segment[] } };
  progenitor_colors: { [line: string]: string };
}

export interface PainterSeg {
  id: string;
  chromosome: number;
  start: number;
  end: number;
  cm: number;
  snps: number;
  match_name: string;
  match_source?: string;
  group_name?: string;
  group_colour?: string;
  side?: string;
  confidence?: string;
  notes?: string;
  linked_individual_id?: string;
}

export interface ChromosomePainterCombinedBarsProps {
  chromLengths: { [chrom: string]: number };
  progenitorData: ChromPainterData | null;
  activeProgenitors: { [id: string]: boolean };
  painterSegs: PainterSeg[];
  hovered: { chrom: string; seg: Segment } | null;
  setHovered: React.Dispatch<React.SetStateAction<{ chrom: string; seg: Segment } | null>>;
  groupHovered: PainterSeg | null;
  setGroupHovered: React.Dispatch<React.SetStateAction<PainterSeg | null>>;
  /** When `mrca_lineage_path`, DNA Painter bars use MRCA codes (e.g. PPPMPP) from linked individuals. */
  painterColorMode?: 'dna_painter_group' | 'mrca_lineage_path';
  /** Path → tree individual id (same as MRCA map); required for MRCA bar colours when mode is mrca_lineage_path. */
  mrcaLinkedIndividualIds?: Record<string, string>;
  /**
   * When `painterColorMode` is `mrca_lineage_path`, set false to colour DNA Painter segments by CSV group
   * instead of MRCA path (legend and tooltips follow the same mode).
   */
  mrcaPathSegmentColorsEnabled?: boolean;
  /** Show progenitor-derived segments on each chromosome row (MRCA tab). */
  showProgenitorSegments?: boolean;
  /** Show DNA Painter match segments on each chromosome row (MRCA tab). */
  showDnaPainterSegments?: boolean;
  /** Underlay AncestryDNA-style regions by parent (maternal vs paternal) for comparison with matches. */
  showAncestryRegionsByParent?: boolean;
}

const ChromosomePainterCombinedBarsPainterTooltip: React.FC<{
  seg: PainterSeg;
  painterColorMode: 'dna_painter_group' | 'mrca_lineage_path';
  winningPathByIndividualId: Record<string, string>;
}> = ({ seg, painterColorMode, winningPathByIndividualId }) => {
  const path =
    painterColorMode === 'mrca_lineage_path' && seg.linked_individual_id
      ? winningPathByIndividualId[seg.linked_individual_id]
      : undefined;
  const pathFill = path ? chromosomePainterMrcaPathBarColors[path] : undefined;
  return (
    <>
      <div>
        <strong>Match:</strong> {seg.match_name}
        {seg.match_source ? <span style={{ color: '#888' }}> ({seg.match_source})</span> : ''}
      </div>
      {painterColorMode === 'mrca_lineage_path' && path ? (
        <div>
          <strong>MRCA path:</strong> <span style={{ color: pathFill }}>{path}</span>
        </div>
      ) : (
        <div>
          <strong>Group:</strong>{' '}
          <span style={{ color: seg.group_colour || '#888' }}>{seg.group_name || '—'}</span>
        </div>
      )}
      {painterColorMode === 'mrca_lineage_path' && seg.group_name ? (
        <div style={{ color: '#666', fontSize: 13 }}>DNA Painter group: {seg.group_name}</div>
      ) : null}
      <div>
        <strong>Chr {seg.chromosome}:</strong> {seg.start.toLocaleString()} – {seg.end.toLocaleString()}
      </div>
      <div>
        <strong>cM:</strong> {seg.cm} &nbsp; <strong>SNPs:</strong> {seg.snps}
      </div>
    </>
  );
};

/**
 * Paternal / maternal / unknown rows with progenitor-coloured segments and DNA Painter group segments.
 */
const ChromosomePainterCombinedBars: React.FC<ChromosomePainterCombinedBarsProps> = ({
  chromLengths,
  progenitorData,
  activeProgenitors,
  painterSegs,
  hovered,
  setHovered,
  groupHovered,
  setGroupHovered,
  painterColorMode = 'dna_painter_group',
  mrcaLinkedIndividualIds = {},
  mrcaPathSegmentColorsEnabled = true,
  showProgenitorSegments = true,
  showDnaPainterSegments = true,
  showAncestryRegionsByParent = false,
}) => {
  const [ancestryRegionHovered, setAncestryRegionHovered] = useState<AncestryRegionByParentSeg | null>(null);

  const effectiveColorMode: 'dna_painter_group' | 'mrca_lineage_path' =
    painterColorMode === 'mrca_lineage_path' && !mrcaPathSegmentColorsEnabled
      ? 'dna_painter_group'
      : painterColorMode;

  const winningPathByIndividualId = useMemo(() => {
    if (effectiveColorMode !== 'mrca_lineage_path') return {} as Record<string, string>;
    return computeWinningPathByIndividualId(mrcaLinkedIndividualIds).winningPathByIndividualId;
  }, [effectiveColorMode, mrcaLinkedIndividualIds]);

  const mrcaLegendPaths = useMemo(() => {
    if (effectiveColorMode !== 'mrca_lineage_path') return [] as string[];
    return Object.entries(mrcaLinkedIndividualIds)
      .filter(([, id]) => id && String(id).trim())
      .map(([p]) => p)
      .sort((a, b) => a.length - b.length || a.localeCompare(b));
  }, [effectiveColorMode, mrcaLinkedIndividualIds]);

  const painterBar = (seg: PainterSeg) => {
    if (effectiveColorMode !== 'mrca_lineage_path') {
      return {
        fill: seg.group_colour || '#888',
        title: `${seg.match_name} | ${seg.group_name || '—'} | ${seg.cm} cM`,
      };
    }
    const lid = seg.linked_individual_id;
    const path = lid ? winningPathByIndividualId[lid] : undefined;
    if (path) {
      return {
        fill: chromosomePainterMrcaPathBarColors[path] || '#888',
        title: `${seg.match_name} | MRCA ${path} | ${seg.cm} cM`,
      };
    }
    return {
      fill: seg.group_colour || '#757575',
      title: `${seg.match_name} | ${seg.group_name || 'Unlinked'} | ${seg.cm} cM`,
    };
  };

  if (!progenitorData && painterSegs.length === 0 && !showAncestryRegionsByParent) return null;

  if (!showProgenitorSegments && !showDnaPainterSegments && !showAncestryRegionsByParent) {
    return (
      <div
        className="chromosome-painter-combined-bars-no-layers-notice"
        style={{
          overflowX: 'auto',
          background: '#23272f',
          borderRadius: 12,
          padding: 24,
          color: '#bdbdbd',
          fontSize: 14,
        }}
      >
        Turn on <strong style={{ color: '#e0e0e0' }}>Progenitor lines</strong>,{' '}
        <strong style={{ color: '#e0e0e0' }}>DNA Painter segments</strong>, and/or{' '}
        <strong style={{ color: '#e0e0e0' }}>Ancestry regions by parent</strong> to see chromosome bars.
      </div>
    );
  }

  return (
    <div
      className="chromosome-painter-combined-bars-root"
      style={{ overflowX: 'auto', background: '#23272f', borderRadius: 12, padding: 24 }}
    >
      {effectiveColorMode === 'mrca_lineage_path' && showDnaPainterSegments && mrcaLegendPaths.length > 0 && (
        <div
          className="chromosome-painter-combined-bars-mrca-path-legend"
          style={{
            marginBottom: 18,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '10px 14px',
            color: '#e0e0e0',
            fontSize: 13,
          }}
        >
          <span style={{ fontWeight: 700, color: '#fff' }}>MRCA paths (DNA Painter segment colours)</span>
          {mrcaLegendPaths.map((path) => (
            <span
              key={path}
              className="chromosome-painter-combined-bars-mrca-path-legend-item"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  background: chromosomePainterMrcaPathBarColors[path] || '#888',
                  border: '1px solid rgba(255,255,255,0.25)',
                }}
              />
              <span style={{ fontWeight: 600, fontFamily: 'ui-monospace, monospace' }}>{path}</span>
            </span>
          ))}
        </div>
      )}
      {Object.keys(chromLengths).map((chrom) => {
        const progPaternalRaw = progenitorData
          ? (progenitorData.chromosomes[chrom]?.paternal || []).filter((s: Segment) => activeProgenitors[s.progenitor_id])
          : [];
        const progMaternalRaw = progenitorData
          ? (progenitorData.chromosomes[chrom]?.maternal || []).filter((s: Segment) => activeProgenitors[s.progenitor_id])
          : [];
        const paintPaternalAll = painterSegs.filter((s) => String(s.chromosome) === chrom && s.side === 'Paternal');
        const paintMaternalAll = painterSegs.filter((s) => String(s.chromosome) === chrom && s.side === 'Maternal');
        const paintUnknownAll = painterSegs.filter(
          (s) => String(s.chromosome) === chrom && s.side !== 'Paternal' && s.side !== 'Maternal'
        );
        const progPaternal = showProgenitorSegments ? progPaternalRaw : [];
        const progMaternal = showProgenitorSegments ? progMaternalRaw : [];
        const paintPaternal = showDnaPainterSegments ? paintPaternalAll : [];
        const paintMaternal = showDnaPainterSegments ? paintMaternalAll : [];
        const paintUnknown = showDnaPainterSegments ? paintUnknownAll : [];

        const len = chromLengths[chrom];
        const showAncPaternalRow =
          showAncestryRegionsByParent &&
          ANCESTRY_REGIONS_BY_PARENT.some(
            (s) => String(s.chromosome) === chrom && s.side === 'Paternal'
          );
        const showAncMaternalRow =
          showAncestryRegionsByParent &&
          ANCESTRY_REGIONS_BY_PARENT.some(
            (s) => String(s.chromosome) === chrom && s.side === 'Maternal'
          );
        const showPaternalRow =
          progPaternal.length > 0 || paintPaternal.length > 0 || showAncPaternalRow;
        const showMaternalRow =
          progMaternal.length > 0 || paintMaternal.length > 0 || showAncMaternalRow;
        const showUnknownRow = paintUnknown.length > 0;
        if (!showPaternalRow && !showMaternalRow && !showUnknownRow) return null;

        return (
          <div key={chrom} style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 600, color: '#fff', marginBottom: 2 }}>Chr {chrom}</div>

            {showPaternalRow && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ width: 70, color: '#b2dfdb', fontWeight: 500, fontSize: 13 }}>Paternal</span>
                <div style={{ position: 'relative', height: 20, background: '#444', borderRadius: 8, flex: 1 }}>
                  <ChromosomePainterAncestryRegionLayer
                    chrom={chrom}
                    chromLength={len}
                    side="Paternal"
                    enabled={showAncestryRegionsByParent}
                    hovered={ancestryRegionHovered}
                    setHovered={setAncestryRegionHovered}
                  />
                  {progPaternal.map((seg: Segment) => {
                    const left = (seg.start / len) * 100;
                    const width = ((seg.end - seg.start) / len) * 100;
                    const isHov = hovered?.chrom === chrom && hovered.seg.segment_id === seg.segment_id;
                    return (
                      <div
                        key={seg.segment_id}
                        style={{
                          position: 'absolute',
                          left: `${left}%`,
                          width: `${Math.max(width, 0.2)}%`,
                          height: 16,
                          background: seg.progenitor_color,
                          borderRadius: 6,
                          opacity: isHov ? 1 : 0.85,
                          border: isHov ? '2px solid #fff' : 'none',
                          cursor: 'pointer',
                          zIndex: isHov ? 2 : 1,
                        }}
                        onMouseEnter={() => setHovered({ chrom, seg })}
                        onMouseLeave={() => setHovered(null)}
                        title={`${seg.progenitor_name}: ${seg.match_name} (${seg.centimorgans} cM)`}
                      />
                    );
                  })}
                  {paintPaternal.map((seg) => {
                    const left = (seg.start / len) * 100;
                    const width = ((seg.end - seg.start) / len) * 100;
                    const isHov = groupHovered?.id === seg.id;
                    const pb = painterBar(seg);
                    return (
                      <div
                        key={seg.id}
                        style={{
                          position: 'absolute',
                          left: `${left}%`,
                          width: `${Math.max(width, 0.2)}%`,
                          height: 16,
                          background: pb.fill,
                          borderRadius: 6,
                          opacity: isHov ? 1 : 0.75,
                          border: isHov ? '2px solid #fff' : 'none',
                          cursor: 'pointer',
                          zIndex: isHov ? 2 : 1,
                        }}
                        onMouseEnter={() => setGroupHovered(seg)}
                        onMouseLeave={() => setGroupHovered(null)}
                        title={pb.title}
                      />
                    );
                  })}
                  {hovered?.chrom === chrom && progPaternal.some((s: Segment) => s.segment_id === hovered.seg.segment_id) && (
                    <div
                      className="genealogy-card"
                      style={{
                        position: 'absolute',
                        left: `${(hovered.seg.start / len) * 100}%`,
                        top: 22,
                        color: '#222',
                        borderRadius: 6,
                        padding: '8px 14px',
                        fontSize: 14,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                        zIndex: 10,
                        minWidth: 220,
                        background: '#fff',
                      }}
                    >
                      <div>
                        <strong>Progenitor:</strong>{' '}
                        <span style={{ color: hovered.seg.progenitor_color }}>{hovered.seg.progenitor_name}</span>
                      </div>
                      <div>
                        <strong>Match:</strong> {hovered.seg.match_name}
                      </div>
                      <div>
                        <strong>Chr {hovered.seg.chromosome}:</strong> {hovered.seg.start.toLocaleString()} –{' '}
                        {hovered.seg.end.toLocaleString()}
                      </div>
                      <div>
                        <strong>cM:</strong> {hovered.seg.centimorgans} &nbsp; <strong>SNPs:</strong> {hovered.seg.snps}
                      </div>
                    </div>
                  )}
                  {groupHovered &&
                    String(groupHovered.chromosome) === chrom &&
                    paintPaternal.some((s) => s.id === groupHovered.id) && (
                      <div
                        className="genealogy-card"
                        style={{
                          position: 'absolute',
                          left: `${(groupHovered.start / len) * 100}%`,
                          top: 22,
                          color: '#222',
                          borderRadius: 6,
                          padding: '8px 14px',
                          fontSize: 14,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                          zIndex: 10,
                          minWidth: 240,
                          background: '#fff',
                        }}
                      >
                        <ChromosomePainterCombinedBarsPainterTooltip
                          seg={groupHovered}
                          painterColorMode={effectiveColorMode}
                          winningPathByIndividualId={winningPathByIndividualId}
                        />
                      </div>
                    )}
                </div>
              </div>
            )}

            {showMaternalRow && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ width: 70, color: '#f8bbd0', fontWeight: 500, fontSize: 13 }}>Maternal</span>
                <div style={{ position: 'relative', height: 20, background: '#444', borderRadius: 8, flex: 1 }}>
                  <ChromosomePainterAncestryRegionLayer
                    chrom={chrom}
                    chromLength={len}
                    side="Maternal"
                    enabled={showAncestryRegionsByParent}
                    hovered={ancestryRegionHovered}
                    setHovered={setAncestryRegionHovered}
                  />
                  {progMaternal.map((seg: Segment) => {
                    const left = (seg.start / len) * 100;
                    const width = ((seg.end - seg.start) / len) * 100;
                    const isHov = hovered?.chrom === chrom && hovered.seg.segment_id === seg.segment_id;
                    return (
                      <div
                        key={seg.segment_id}
                        style={{
                          position: 'absolute',
                          left: `${left}%`,
                          width: `${Math.max(width, 0.2)}%`,
                          height: 16,
                          background: seg.progenitor_color,
                          borderRadius: 6,
                          opacity: isHov ? 1 : 0.85,
                          border: isHov ? '2px solid #fff' : 'none',
                          cursor: 'pointer',
                          zIndex: isHov ? 2 : 1,
                        }}
                        onMouseEnter={() => setHovered({ chrom, seg })}
                        onMouseLeave={() => setHovered(null)}
                        title={`${seg.progenitor_name}: ${seg.match_name} (${seg.centimorgans} cM)`}
                      />
                    );
                  })}
                  {paintMaternal.map((seg) => {
                    const left = (seg.start / len) * 100;
                    const width = ((seg.end - seg.start) / len) * 100;
                    const isHov = groupHovered?.id === seg.id;
                    const pb = painterBar(seg);
                    return (
                      <div
                        key={seg.id}
                        style={{
                          position: 'absolute',
                          left: `${left}%`,
                          width: `${Math.max(width, 0.2)}%`,
                          height: 16,
                          background: pb.fill,
                          borderRadius: 6,
                          opacity: isHov ? 1 : 0.75,
                          border: isHov ? '2px solid #fff' : 'none',
                          cursor: 'pointer',
                          zIndex: isHov ? 2 : 1,
                        }}
                        onMouseEnter={() => setGroupHovered(seg)}
                        onMouseLeave={() => setGroupHovered(null)}
                        title={pb.title}
                      />
                    );
                  })}
                  {hovered?.chrom === chrom && progMaternal.some((s: Segment) => s.segment_id === hovered.seg.segment_id) && (
                    <div
                      className="genealogy-card"
                      style={{
                        position: 'absolute',
                        left: `${(hovered.seg.start / len) * 100}%`,
                        top: 22,
                        color: '#222',
                        borderRadius: 6,
                        padding: '8px 14px',
                        fontSize: 14,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                        zIndex: 10,
                        minWidth: 220,
                        background: '#fff',
                      }}
                    >
                      <div>
                        <strong>Progenitor:</strong>{' '}
                        <span style={{ color: hovered.seg.progenitor_color }}>{hovered.seg.progenitor_name}</span>
                      </div>
                      <div>
                        <strong>Match:</strong> {hovered.seg.match_name}
                      </div>
                      <div>
                        <strong>Chr {hovered.seg.chromosome}:</strong> {hovered.seg.start.toLocaleString()} –{' '}
                        {hovered.seg.end.toLocaleString()}
                      </div>
                      <div>
                        <strong>cM:</strong> {hovered.seg.centimorgans} &nbsp; <strong>SNPs:</strong> {hovered.seg.snps}
                      </div>
                    </div>
                  )}
                  {groupHovered &&
                    String(groupHovered.chromosome) === chrom &&
                    paintMaternal.some((s) => s.id === groupHovered.id) && (
                      <div
                        className="genealogy-card"
                        style={{
                          position: 'absolute',
                          left: `${(groupHovered.start / len) * 100}%`,
                          top: 22,
                          color: '#222',
                          borderRadius: 6,
                          padding: '8px 14px',
                          fontSize: 14,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                          zIndex: 10,
                          minWidth: 240,
                          background: '#fff',
                        }}
                      >
                        <ChromosomePainterCombinedBarsPainterTooltip
                          seg={groupHovered}
                          painterColorMode={effectiveColorMode}
                          winningPathByIndividualId={winningPathByIndividualId}
                        />
                      </div>
                    )}
                </div>
              </div>
            )}

            {paintUnknown.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ width: 70, color: '#bdbdbd', fontWeight: 500, fontSize: 13 }}>Unknown</span>
                <div style={{ position: 'relative', height: 20, background: '#444', borderRadius: 8, flex: 1 }}>
                  {paintUnknown.map((seg) => {
                    const left = (seg.start / len) * 100;
                    const width = ((seg.end - seg.start) / len) * 100;
                    const isHov = groupHovered?.id === seg.id;
                    const pb = painterBar(seg);
                    return (
                      <div
                        key={seg.id}
                        style={{
                          position: 'absolute',
                          left: `${left}%`,
                          width: `${Math.max(width, 0.2)}%`,
                          height: 16,
                          background: pb.fill,
                          borderRadius: 6,
                          opacity: isHov ? 1 : 0.75,
                          border: isHov ? '2px solid #fff' : 'none',
                          cursor: 'pointer',
                          zIndex: isHov ? 2 : 1,
                        }}
                        onMouseEnter={() => setGroupHovered(seg)}
                        onMouseLeave={() => setGroupHovered(null)}
                        title={pb.title}
                      />
                    );
                  })}
                  {groupHovered &&
                    String(groupHovered.chromosome) === chrom &&
                    paintUnknown.some((s) => s.id === groupHovered.id) && (
                      <div
                        className="genealogy-card"
                        style={{
                          position: 'absolute',
                          left: `${(groupHovered.start / len) * 100}%`,
                          top: 22,
                          color: '#222',
                          borderRadius: 6,
                          padding: '8px 14px',
                          fontSize: 14,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                          zIndex: 10,
                          minWidth: 240,
                          background: '#fff',
                        }}
                      >
                        <ChromosomePainterCombinedBarsPainterTooltip
                          seg={groupHovered}
                          painterColorMode={effectiveColorMode}
                          winningPathByIndividualId={winningPathByIndividualId}
                        />
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ChromosomePainterCombinedBars;
